import { useEffect, useMemo, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

import {
  activeViewIdAtomFamily,
  activeViewSF,
  areAllIdsInTableSelection,
  bulkGridSelectionModeAF,
  BulkGridSelectionModeEnum,
  bulkGridSelectionPendingRowsApplyAF,
  bulkGridSelectionRowCountAF,
  bulkGridSelectionRowCountEditingAF,
  bulkGridSelectionServerScopeIdsAF,
  getTableSelectionLimit,
  GROUPING_GRID_NO_GROUPS,
  inferBulkGridSelectionMode,
  isEntityServerSidePaginated,
  isEntityWithGridMapBulkAutofit,
  NO_GROUPING_QS_VALUE,
  removeIdsFromTableSelection,
  resolveFirstNRowIdsFromVisibleGrid,
  resolveUngroupedPageSelectionAfterChange,
  selectedIdsSF,
  selectedTableRowsIdsSetSF,
  setsEqual,
  shouldResetBulkSelectionOnGroupingChange,
  shouldResetBulkSelectionOnSortingChange,
  tableBulkSelectionAllIdsSF,
  tableBulkSelectionCurrentPageIdsSF,
  tableBulkSelectionRowCountIdsSF,
  tableColumnsSortingAF,
  tableGroupingAF,
  tableItemPaginationAF,
  tableVisibleRowIdsForSelectAllSF,
  warnTableSelectionLimitReached,
  type IUngroupedPageSelectionSnapshot,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useLiveMapAutofitBulkGate } from "./rides/use-live-map-autofit-bulk-gate";
import { useFetchServerPaginatedBulkSelectionIds } from "./use-fetch-server-paginated-bulk-selection-ids";

interface IUseBulkGridSelectionArguments {
  isSelectRowsEnabled?: boolean;
  isSelectionMenuEnabled?: boolean;
}

interface IUseBulkGridSelectionResult {
  activeMode: BulkGridSelectionModeEnum;
  rowCount: number;
  isCheckboxChecked: boolean;
  showRowCountInput: boolean;
  setRowCount: (value: number) => void;
  applyMode: (mode: BulkGridSelectionModeEnum) => void | Promise<void>;
  toggleCheckbox: () => boolean | Promise<boolean>;
}

export const useBulkGridSelection = ({
  isSelectRowsEnabled = true,
  isSelectionMenuEnabled = true,
}: IUseBulkGridSelectionArguments = {}): IUseBulkGridSelectionResult => {
  const { entity } = usePageContext();
  const { fetchBulkSelectionIds } = useFetchServerPaginatedBulkSelectionIds();
  const { runBulkSelectionDeferringMapRender } = useLiveMapAutofitBulkGate();
  const isGridMapBulkAutofitEntity = isEntityWithGridMapBulkAutofit(entity);
  const isServerPaginated = isEntityServerSidePaginated(entity);

  const activeMode = useRecoilValue(bulkGridSelectionModeAF(entity));
  const rowCount = useRecoilValue(bulkGridSelectionRowCountAF(entity));
  const pendingRowsApply = useRecoilValue(
    bulkGridSelectionPendingRowsApplyAF(entity),
  );
  const isRowCountEditing = useRecoilValue(
    bulkGridSelectionRowCountEditingAF(entity),
  );
  const columnGrouping = useRecoilValue(tableGroupingAF(entity));
  const columnSorting = useRecoilValue(tableColumnsSortingAF(entity));

  const allIds = useRecoilValue(tableBulkSelectionAllIdsSF(entity));
  const pageIds = useRecoilValue(tableBulkSelectionCurrentPageIdsSF(entity));
  const rowCountIds = useRecoilValue(tableBulkSelectionRowCountIdsSF(entity));
  const visibleRowIds = useRecoilValue(
    tableVisibleRowIdsForSelectAllSF(entity),
  );

  const selectedIds = useRecoilValue(selectedIdsSF(entity));
  const selectedIdsSet = useRecoilValue(selectedTableRowsIdsSetSF(entity));
  const setSelectedIds = useSetRecoilState(selectedIdsSF(entity));
  const setActiveMode = useSetRecoilState(bulkGridSelectionModeAF(entity));
  const setRowCountState = useSetRecoilState(
    bulkGridSelectionRowCountAF(entity),
  );
  const setPendingRowsApply = useSetRecoilState(
    bulkGridSelectionPendingRowsApplyAF(entity),
  );
  const setColumnGrouping = useSetRecoilState(tableGroupingAF(entity));
  const setActiveViewId = useSetRecoilState(activeViewIdAtomFamily(entity));
  const activeView = useRecoilValue(activeViewSF(entity));
  const resetPendingRowsApply = useResetRecoilState(
    bulkGridSelectionPendingRowsApplyAF(entity),
  );
  const setServerScopeIds = useSetRecoilState(
    bulkGridSelectionServerScopeIdsAF(entity),
  );
  const resetServerScopeIds = useResetRecoilState(
    bulkGridSelectionServerScopeIdsAF(entity),
  );

  const isGridUngrouped =
    !columnGrouping?.length || columnGrouping[0] === NO_GROUPING_QS_VALUE;
  const ungroupedPagination = useRecoilValue(
    tableItemPaginationAF([GROUPING_GRID_NO_GROUPS, entity]),
  );
  const ungroupedPageSelectionRef =
    useRef<IUngroupedPageSelectionSnapshot | null>(null);
  const previousGroupingKeyRef = useRef<string | null>(null);
  const groupingKey = (columnGrouping ?? []).join("|");
  const previousSortingKeyRef = useRef<string | null>(null);
  const sortingKey = (columnSorting ?? [])
    .map(sort => `${sort.id}:${sort.desc ? "desc" : "asc"}`)
    .join("|");

  const captureUngroupedPageSelection = useEvent((ids: number[]): void => {
    if (!isGridUngrouped || !ungroupedPagination) {
      return;
    }

    ungroupedPageSelectionRef.current = {
      pageIndex: ungroupedPagination.pageIndex,
      pageSize: ungroupedPagination.pageSize,
      ids: [...ids],
    };
  });

  const clearUngroupedPageSelection = useEvent((): void => {
    ungroupedPageSelectionRef.current = null;
  });

  const scopeIds = useMemo((): number[] => {
    if (!isSelectionMenuEnabled) {
      return allIds;
    }

    switch (activeMode) {
      case BulkGridSelectionModeEnum.Page:
        return pageIds;
      case BulkGridSelectionModeEnum.Rows:
        return rowCountIds;
      default:
        return allIds;
    }
  }, [activeMode, allIds, isSelectionMenuEnabled, pageIds, rowCountIds]);

  const isCheckboxChecked = useMemo(() => {
    return areAllIdsInTableSelection(selectedIdsSet, scopeIds);
  }, [scopeIds, selectedIdsSet]);

  const cancelGridGrouping = useEvent((): void => {
    setColumnGrouping([NO_GROUPING_QS_VALUE]);

    if (
      activeView &&
      activeView.viewState.columnsGrouping?.at(0) !== NO_GROUPING_QS_VALUE
    ) {
      setActiveViewId(undefined);
    }
  });

  const replaceSelectionWithIds = useEvent(
    (ids: number[], serverScope?: number[]): boolean => {
      const applySelection = (): void => {
        if (isServerPaginated && serverScope !== undefined) {
          setServerScopeIds(serverScope);
        }

        setSelectedIds(ids);
      };

      const limit = getTableSelectionLimit(entity);

      if (limit !== undefined && ids.length > limit) {
        warnTableSelectionLimitReached(entity);

        return false;
      }

      if (isGridMapBulkAutofitEntity) {
        runBulkSelectionDeferringMapRender(applySelection);

        return true;
      }

      applySelection();

      return true;
    },
  );

  const resolveServerScopeIds = useEvent(
    async (mode: BulkGridSelectionModeEnum): Promise<number[]> => {
      if (mode === BulkGridSelectionModeEnum.Page) {
        return pageIds;
      }

      const cached =
        mode === BulkGridSelectionModeEnum.Rows ? rowCountIds : allIds;

      if (cached.length) {
        return cached;
      }

      const fetched = await fetchBulkSelectionIds({
        mode,
        rowCount,
      });

      return fetched ?? [];
    },
  );

  const applyMode = useEvent(
    async (mode: BulkGridSelectionModeEnum): Promise<void> => {
      setActiveMode(mode);

      if (mode === BulkGridSelectionModeEnum.Rows) {
        clearUngroupedPageSelection();
        cancelGridGrouping();
        setSelectedIds([]);
        resetServerScopeIds();
        setPendingRowsApply(true);

        return;
      }

      resetPendingRowsApply();

      if (mode === BulkGridSelectionModeEnum.Page) {
        resetServerScopeIds();
        captureUngroupedPageSelection(pageIds);
        replaceSelectionWithIds(pageIds);

        return;
      }

      clearUngroupedPageSelection();

      if (isServerPaginated) {
        const fetched = await fetchBulkSelectionIds({
          mode: BulkGridSelectionModeEnum.All,
        });

        if (fetched?.length) {
          replaceSelectionWithIds(fetched, fetched);
        }

        return;
      }

      replaceSelectionWithIds(allIds);
    },
  );

  const toggleCheckbox = useEvent(async (): Promise<boolean> => {
    let ids = scopeIds;

    if (
      isServerPaginated &&
      activeMode !== BulkGridSelectionModeEnum.Page &&
      !ids.length
    ) {
      ids = await resolveServerScopeIds(activeMode);
    }

    if (!ids.length) {
      return false;
    }

    if (isCheckboxChecked) {
      const clearScope = (): void => {
        if (activeMode === BulkGridSelectionModeEnum.Page) {
          clearUngroupedPageSelection();
        }

        setSelectedIds(current => removeIdsFromTableSelection(current, ids));

        if (
          isServerPaginated &&
          activeMode !== BulkGridSelectionModeEnum.Page
        ) {
          resetServerScopeIds();
        }
      };

      if (isGridMapBulkAutofitEntity) {
        runBulkSelectionDeferringMapRender(clearScope);

        return true;
      }

      clearScope();

      return true;
    }

    if (activeMode === BulkGridSelectionModeEnum.Page) {
      if (isServerPaginated) {
        resetServerScopeIds();
      }

      captureUngroupedPageSelection(ids);
    }

    const serverScope =
      isServerPaginated && activeMode !== BulkGridSelectionModeEnum.Page
        ? ids
        : undefined;

    return replaceSelectionWithIds(ids, serverScope);
  });

  const setRowCount = useEvent((value: number): void => {
    const normalized = Number.isFinite(value)
      ? Math.max(0, Math.floor(value))
      : 0;
    setRowCountState(normalized);

    if (activeMode === BulkGridSelectionModeEnum.Rows) {
      setPendingRowsApply(true);
    }
  });

  useEffect(() => {
    if (
      !pendingRowsApply ||
      activeMode !== BulkGridSelectionModeEnum.Rows ||
      isRowCountEditing
    ) {
      return;
    }

    if (!isGridUngrouped) {
      return;
    }

    if (isServerPaginated) {
      if (rowCount <= 0) {
        resetServerScopeIds();
        replaceSelectionWithIds([]);
        setPendingRowsApply(false);

        return;
      }

      const pageIndex = isGridUngrouped
        ? (ungroupedPagination?.pageIndex ?? 0)
        : 0;
      const fromVisible = resolveFirstNRowIdsFromVisibleGrid({
        visibleRowIds,
        count: rowCount,
        pageIndex,
      });

      if (fromVisible?.length) {
        replaceSelectionWithIds(fromVisible, fromVisible);
        setPendingRowsApply(false);

        return;
      }

      void (async (): Promise<void> => {
        const fetched = await fetchBulkSelectionIds({
          mode: BulkGridSelectionModeEnum.Rows,
          rowCount,
        });

        if (fetched?.length) {
          replaceSelectionWithIds(fetched, fetched);
        }

        setPendingRowsApply(false);
      })();

      return;
    }

    replaceSelectionWithIds(rowCountIds);
    setPendingRowsApply(false);
  }, [
    activeMode,
    fetchBulkSelectionIds,
    isGridUngrouped,
    isRowCountEditing,
    isServerPaginated,
    pendingRowsApply,
    replaceSelectionWithIds,
    resetServerScopeIds,
    rowCount,
    rowCountIds,
    setPendingRowsApply,
    ungroupedPagination?.pageIndex,
    visibleRowIds,
  ]);

  useEffect(() => {
    if (!isGridUngrouped) {
      return;
    }

    const nextIds = resolveUngroupedPageSelectionAfterChange({
      mode: activeMode,
      snapshot: ungroupedPageSelectionRef.current,
      nextPagination: ungroupedPagination,
    });

    if (nextIds === undefined) {
      return;
    }

    setSelectedIds(nextIds);
  }, [activeMode, isGridUngrouped, setSelectedIds, ungroupedPagination]);

  useEffect(() => {
    const previousGroupingKey = previousGroupingKeyRef.current;
    previousGroupingKeyRef.current = groupingKey;

    const shouldReset = shouldResetBulkSelectionOnGroupingChange({
      previousGroupingKey,
      nextGroupingKey: groupingKey,
      isRowsUngroupInProgress:
        activeMode === BulkGridSelectionModeEnum.Rows && pendingRowsApply,
    });

    if (!shouldReset) {
      return;
    }

    clearUngroupedPageSelection();
    resetServerScopeIds();
    resetPendingRowsApply();
    setSelectedIds([]);
    setActiveMode(BulkGridSelectionModeEnum.All);
  }, [
    activeMode,
    clearUngroupedPageSelection,
    groupingKey,
    pendingRowsApply,
    resetPendingRowsApply,
    resetServerScopeIds,
    setActiveMode,
    setSelectedIds,
  ]);

  useEffect(() => {
    const previousSortingKey = previousSortingKeyRef.current;
    previousSortingKeyRef.current = sortingKey;

    const shouldReset = shouldResetBulkSelectionOnSortingChange({
      previousSortingKey,
      nextSortingKey: sortingKey,
      activeMode,
    });

    if (!shouldReset) {
      return;
    }

    clearUngroupedPageSelection();
    resetServerScopeIds();
    resetPendingRowsApply();
    setSelectedIds([]);
    setActiveMode(BulkGridSelectionModeEnum.All);
  }, [
    activeMode,
    clearUngroupedPageSelection,
    resetPendingRowsApply,
    resetServerScopeIds,
    setActiveMode,
    setSelectedIds,
    sortingKey,
  ]);

  useEffect(() => {
    if (isSelectRowsEnabled || activeMode !== BulkGridSelectionModeEnum.Rows) {
      return;
    }

    setActiveMode(BulkGridSelectionModeEnum.All);
    resetPendingRowsApply();
  }, [activeMode, isSelectRowsEnabled, resetPendingRowsApply, setActiveMode]);

  useEffect(() => {
    if (
      isSelectionMenuEnabled ||
      activeMode === BulkGridSelectionModeEnum.All
    ) {
      return;
    }

    setActiveMode(BulkGridSelectionModeEnum.All);
    resetPendingRowsApply();
  }, [
    activeMode,
    isSelectionMenuEnabled,
    resetPendingRowsApply,
    setActiveMode,
  ]);

  useEffect(() => {
    if (!isSelectionMenuEnabled || pendingRowsApply || isRowCountEditing) {
      return;
    }

    // Keep ROWS only when selection exactly matches the N-row slice (not a
    // superset like Select all, which still contains those row ids).
    if (
      activeMode === BulkGridSelectionModeEnum.Rows &&
      rowCountIds.length > 0 &&
      setsEqual(selectedIdsSet, new Set(rowCountIds))
    ) {
      return;
    }

    const inferredMode = inferBulkGridSelectionMode({
      selectedIds,
      allIds,
      pageIds,
      rowCountIds,
      activeMode,
    });

    if (!inferredMode || inferredMode === activeMode) {
      return;
    }

    setActiveMode(inferredMode);

    if (inferredMode === BulkGridSelectionModeEnum.Rows) {
      setRowCountState(rowCountIds.length);
    }
  }, [
    activeMode,
    allIds,
    isRowCountEditing,
    isSelectionMenuEnabled,
    pageIds,
    pendingRowsApply,
    rowCountIds,
    selectedIds,
    selectedIdsSet,
    setActiveMode,
    setRowCountState,
  ]);

  return {
    activeMode,
    rowCount,
    isCheckboxChecked,
    showRowCountInput:
      isSelectionMenuEnabled && activeMode === BulkGridSelectionModeEnum.Rows,
    setRowCount,
    applyMode,
    toggleCheckbox,
  };
};
